import * as Yup from "yup";
import { Form, Formik } from "formik";
import MainLayout from "../layout/main-layout";
import Input from "../Components/input/Input";
import TextArea from "../Components/input/text-area";
import Button from "../Components/input/Button";
import {
  CircleFadingPlus,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Select from "../Components/input/Select";
import axios from "axios";
import MetaTags from "../utils/meta-tags";
import Grid from "../Components/Grid/grid";
import { Link } from "react-router-dom";

function ContactUs() {
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Name is required"),
    contact_number: Yup.string()
      .matches(/^\+?\d{7,15}$/, "Enter a valid phone number")
      .required("Contact number is required"),
    purpose: Yup.string().required("Select Purpose for contact."),
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  });

  const purposes = [
    { id: 4, name: "Hire Dedicated Resources" },
    { id: 5, name: "Custom Software Development" },
    { id: 6, name: "IT Consulting" },
    { id: 7, name: "Website Design & Development" },
    { id: 8, name: "Mobile Application Development" },
    { id: 9, name: "Other" },
  ];

  const sendQuery = (values: any, { resetForm }: any) => {
    axios
      .post("contact_us", {
        contact_us: {
          name: values.name,
          email: values.email,
          contact_no: values.contact_number,
          company_name: values.company_name,
          purpose: values.purpose,
          message: values.message,
        },
      })
      .then((res) => {
        toast.success("Query sent successfully");
        resetForm();
      })
      .catch((err) => {
        toast.error(err.response.data.message || err.message);
        console.log(err);
      });
  };

  return (
    <MainLayout>
      <MetaTags
        title="Contact Us | Start Your Project Today | SoftCoding Solutions"
        description="Have a project in mind? Get in touch with SoftCoding Solutions for expert software development consulting, support, or dedicated development teams"
        canonical="https://softcodingsolutions.com/contact-us"
        og={{
          title: "Contact Us | Start Your Project with SoftCoding Solutions",
          description:
            "Get in touch with our team to discuss your software development needs. We're ready to help you build, scale, or support your app.",
          url: "https://softcodingsolutions.com/contact-us",
          type: "website",
          site_name: "SoftCoding Solutions",
          image:
            "https://softcodingsolutions.com/static/media/logo%20company.23b84ead7179e68dfe2f.png",
          imageWidth: "1200",
          imageHeight: "630",
        }}
      />

      <section className="min-h-screen flex items-center justify-center py-40 px-4 w-full">
        <Grid direction="top-center" size={100} />
        <div className="w-full flex flex-col items-center">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">
              Contact Us
            </h1>
            <p className="text-gray-600 text-lg">
              Get in touch and let us know how we can help.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 w-full max-w-6xl">
            <div className="w-full flex items-center justify-center">
              <motion.div
                className="w-full mx-auto my-auto shadow-[6px_6px_0_#000] border-2 p-5 border-black bg-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Formik
                  initialValues={{
                    name: "",
                    contact_number: "",
                    email: "",
                    purpose: "",
                    company_name: "",
                    message: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={sendQuery}
                >
                  {() => (
                    <Form className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Input
                          name="name"
                          type="text"
                          placeholder="Your Name"
                          label="Name"
                        />
                        <Input
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          label="Email"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Input
                          name="contact_number"
                          type="tel"
                          placeholder="Phone Number"
                          label="Phone"
                        />
                        <Input
                          name="company_name"
                          type="text"
                          placeholder="Company Name"
                          label="Company (Optional)"
                        />
                      </div>

                      <Select
                        name="purpose"
                        data={purposes}
                        selectedOption="Select Purpose"
                        label="Purpose"
                      />

                      <TextArea
                        name="message"
                        placeholder="Tell us about your project..."
                        label="Message"
                      />

                      <Button text="Send Message" type="submit" />
                    </Form>
                  )}
                </Formik>
              </motion.div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-xl font-medium text-gray-900 mb-6">
                  Get in Touch
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Mail className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">Email</p>
                      <a
                        href="mailto:support@softcodingsolutions.com"
                        className="text-gray-600 hover:text-gray-900 transition-colors max-w-md break-all"
                      >
                        support@softcodingsolutions.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">Address</p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        518 Solaris Business Hub
                        <br />
                        Nr Bhuyangdev Cross Road
                        <br />
                        Ahmedabad, Gujarat 380061
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <CircleFadingPlus className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-900 font-medium">
                        Follow Us On Social Media
                      </p>
                      <ul className="flex space-x-4 mt-2">
                        <li>
                          <Link
                            to="https://www.instagram.com/softcodingsolution?igsh=MTYwNHduYnl1ZGZ0"
                            target="_blank"
                            className="text-[#161616] hover:text-primary"
                          >
                            <Instagram size={20} />
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="https://www.linkedin.com/company/softcoding-solutions/"
                            target="_blank"
                            className="text-[#161616] hover:text-primary"
                          >
                            <Linkedin size={20} />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              <div className="pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  We typically respond within 24 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}

export default ContactUs;
